export type Role = "customer" | "driver" | "carrier" | "internal";

export type StaffLevel = "admin" | "manager" | "member";

export type Department = "dispatch" | "safety" | "maintenance" | "accounting" | "hr";

export interface AppUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  /** Internal staff only: tier within the org. */
  staffLevel?: StaffLevel | null;
  /** Internal staff only: the department they belong to. */
  department?: Department | null;
}

export const STAFF_LEVEL_LABEL: Record<StaffLevel, string> = {
  admin: "Admin",
  manager: "Manager",
  member: "Member",
};

export const DEPARTMENT_LABEL: Record<Department, string> = {
  dispatch: "Dispatch",
  safety: "Safety & Compliance",
  maintenance: "Maintenance & Operations",
  accounting: "Accounting",
  hr: "HR / Recruiting",
};

/** Can this user open and use the Users admin page? */
export function canManageUsers(user: Pick<AppUser, "role" | "staffLevel"> | null | undefined): boolean {
  return user?.role === "internal" && (user.staffLevel === "admin" || user.staffLevel === "manager");
}

/** Can this staff user manage recruiting/admin workflows like jobs and applications? */
export function canManageRecruiting(
  user: Pick<AppUser, "role" | "staffLevel"> | null | undefined,
): boolean {
  return user?.role === "internal" && (user.staffLevel === "admin" || user.staffLevel === "manager");
}

/** Can this staff user review scoped activity/audit logs? */
export function canViewActivityLog(
  user: Pick<AppUser, "role" | "staffLevel"> | null | undefined,
): boolean {
  return user?.role === "internal" && (user.staffLevel === "admin" || user.staffLevel === "manager");
}

/**
 * Can this staff user see operational views (Loads, Fleet)? HR / Recruiting has no
 * operational role, so those are hidden for HR staff. Admins always see everything.
 */
export function canViewOperations(
  user: Pick<AppUser, "role" | "staffLevel" | "department"> | null | undefined,
): boolean {
  if (user?.role !== "internal") return false;
  if (user.staffLevel === "admin") return true;
  return user.department !== "hr";
}

/**
 * Can this staff user open KPIs? Admin-only until management widens it (Q8).
 * Keep in sync with `kpiDepartmentsForStaff` in `@/features/kpi/access`.
 */
export function canViewKpis(
  user: Pick<AppUser, "role" | "staffLevel" | "department"> | null | undefined,
): boolean {
  return user?.role === "internal" && user.staffLevel === "admin";
}

/** Short human label for who's signed in: department + tier for staff, else role. */
export function describeUserRole(
  user: Pick<AppUser, "role" | "staffLevel" | "department"> | null | undefined,
): string {
  if (!user) return "";
  if (user.role !== "internal") return ROLE_LABEL[user.role];
  if (user.staffLevel === "admin") return "Admin";
  if (user.staffLevel && user.department) {
    return `${DEPARTMENT_LABEL[user.department]} ${STAFF_LEVEL_LABEL[user.staffLevel]}`;
  }
  return ROLE_LABEL.internal;
}

/** Two-letter initials from a name or email, for avatar chips. */
export function userInitials(value: string): string {
  const parts = value.replace(/@.*/, "").split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export const ROLE_HOME: Record<Role, string> = {
  customer: "/app/customer",
  driver: "/app/driver",
  carrier: "/app/carrier",
  internal: "/app/internal",
};

// Role keys stay stable (routes/RLS); only display labels reflect the business
// model: BB is the carrier, so the "carrier" role = leased-on owner-operators.
export const ROLE_LABEL: Record<Role, string> = {
  customer: "Customer",
  driver: "Driver",
  carrier: "Owner-operator",
  internal: "Staff",
};
